/**
 * "Closing sidebar" — opens the SAME panel the closing calendar opens
 * (`ClosingDetailDrawer`), but from the loan itself, so a closer working a file
 * can read its readiness without leaving the drilldown.
 *
 * Identical panel, one difference: "Open loan file" is disabled — you are
 * already on the loan (`disableOpenLoan`). The drawer normally takes a schedule
 * CARD from `GET /closing/schedule`; here we seed it from the loan in Redux and
 * the drawer's own readiness fetch fills in the rest (it merges
 * `readinessToCard(readiness)` over whatever it was handed).
 *
 * Who sees it: admins / super admins, the closing desk (`isCloser`), and the
 * loan's own assigned closer (`loanCloser`) — nobody else needs a closing
 * cockpit on a loan they don't close.
 *
 * The trigger itself is supplied by the host (`renderTrigger`), because the one
 * host is the drilldown's right icon rail and its buttons have their own chrome
 * — this component owns the panel and the permission gate, not the button.
 */
import { useCallback, useMemo, useState } from "react";

import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import ClosingDetailDrawer from "../../LoanClosingsCalendarScreen/ClosingDetailDrawer";
import { usePermissionsHook } from "../../../hooks/usePermissionsHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { useIsAcquisitions } from "../../../pipelines/PipelineContext";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

const sameEmail = (a, b) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

// Is this user allowed to open the closing panel on this loan?
export const useCanSeeClosingSidebar = (loan) => {
  const { isAdmin, isSuperAdmin, isCloser } = usePermissionsHook();
  const myEmail = useSelector((state) => state?.user?.emailAddress);
  return (
    isAdmin || isSuperAdmin || isCloser || sameEmail(loan?.loanCloser, myEmail)
  );
};

const ClosingSidebarButton = ({ renderTrigger }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const isAcquisitions = useIsAcquisitions();
  const { getLoanById } = useUnderwritingHook();
  const canSee = useCanSeeClosingSidebar(loan);
  const [open, setOpen] = useState(false);
  const dealId = loan?._id;

  // Seed card: enough for the panel's crumb + title while the readiness call is
  // in flight. Same shape as a schedule-feed card (closing-readiness.md §2).
  const card = useMemo(() => {
    if (!dealId) return null;
    const borrowerNames = (Array.isArray(loan?.borrowers) ? loan.borrowers : [])
      .map((b) => [b?.firstName, b?.lastName].filter(Boolean).join(" ").trim())
      .filter(Boolean);
    const propertyAddresses = (Array.isArray(loan?.subjectProperties) ? loan.subjectProperties : [])
      .map((p) => addressLabel(p?.address))
      .filter(Boolean);
    // The date the panel opens on, by the same basis rule the feed uses
    // (closing-readiness.md §2): the firm date when there is one, else the
    // expected date. Readiness replaces it a moment later.
    const firm = isAcquisitions ? loan?.settlementDate : loan?.closingScheduledDate;
    const fundingDate = firm || loan?.expectedClosingDate || null;
    const dateBasis = firm
      ? isAcquisitions
        ? "settlement"
        : "scheduled"
      : loan?.expectedClosingDate
        ? "expected"
        : "none";
    return {
      dealId,
      _id: dealId,
      pipeline: isAcquisitions ? "acquisition" : "preclose",
      mode: isAcquisitions ? "trade" : "closing",
      fundingDate,
      dateBasis,
      loanNumber: loan?.loanNumber,
      syncLoanId: loan?.sync_loan_id,
      loanName: loan?.loanName,
      loanStatus: loan?.loanStatus,
      tradeStatus: loan?.tradeStatus,
      loanProductType: loan?.loanProductType,
      loanAmount: loan?.baseLoanAmount ?? loan?.totalLoanAmount,
      closer: loan?.loanCloser,
      originator: loan?.originator,
      borrowerNames,
      propertyAddresses,
    };
  }, [dealId, isAcquisitions, loan]);

  // A date edit or an upload inside the panel changed the loan — pull it back
  // into Redux so the rest of the drilldown catches up.
  const onChanged = useCallback(() => {
    if (!dealId) return;
    getLoanById({ loanId: dealId, onSuccessFn: (data) => dispatch(loanDrilldownSet(data)) });
  }, [dealId, dispatch, getLoanById]);

  if (!canSee || !card) return null;
  // The two pipelines never share vocabulary: a preclose loan closes, an
  // acquisition settles — same panel, different noun on the rail.
  const label = isAcquisitions ? "Settlement" : "Closing";
  return (
    <>
      {renderTrigger({ onClick: () => setOpen(true), open, label })}
      <ClosingDetailDrawer
        loan={card}
        open={open}
        onClose={() => setOpen(false)}
        onChanged={onChanged}
        disableOpenLoan
      />
    </>
  );
};

ClosingSidebarButton.propTypes = {
  /** `({ onClick, open, label }) => node` — the host's own trigger chrome. */
  renderTrigger: PropTypes.func.isRequired,
};

export default ClosingSidebarButton;
